import { flattenedNavigation } from './_vars';
import SafeHTMLBlock from '@srcDS/components/atoms/safeHTMLBlock';
import clsx from 'clsx';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useMedia, useSessionStorage } from 'react-use';
import { useLocation } from 'react-router-dom';
import { Link } from '../../atoms/link/Link';
import Marquee from '../../molecules/marquee';
import HeaderActions from '../../molecules/headerActions/HeaderActions';
import { HeaderNavigation } from '../../molecules/headerNavigation/HeaderNavigation';
import SearchInput from '@srcDS/components/molecules/searchInput/SearchInput';
import GeoIP from '../../molecules/geoIP/GeoIP';
import CloseCross from '../../atoms/icons/CloseCross';
import Button from '../../atoms/button';
import { ResponsiveImage } from '../../atoms/image/ResponsiveImage';
import { useStickyHeader } from '@srcDS/hooks/useStickyHeader';
import { useLogo } from '@srcDS/hooks/useLogo';
import { useScrollDirection } from '@srcDS/hooks/useScrollDirection';
import { useHeaderEffects } from '@srcDS/components/organisms/header/hooks/useHeaderEffects';
import { useHeaderNavigation } from '@srcDS/components/organisms/header/hooks/useHeaderNavigation';
import { useNotifications } from '@srcDS/components/organisms/header/hooks/useNotifications';
import useGeoIpBanner from '@srcDS/components/molecules/geoIP/useGeoIpBanner';
import { useLayoutContext } from '@srcDS/provider/layout/LayoutContext';
import { convertTextToId } from '@srcDS/utils/convertTextToId';
import { SecondaryMenuLevel } from '@apgnext/frontend/legacy/js/components/molecules/headerMenu/desktopMenu/secondaryMenuLevel/SecondaryMenuLevel';
import NotificationBar from '@srcDS/components/molecules/notificationBar/NotificationBar';
import { CrossBrandTraffic } from '@srcDS/components/organisms/crossBrandTraffic/CrossBrandTraffic';
import {
  Brand,
  CrossBrandTrafficProps,
  useCrossBrandTraffic,
} from '@srcDS/components/organisms/crossBrandTraffic/hooks/useCrossBrandTraffic';
import styles from '@srcDS/components/organisms/header/Header.module.scss';
import { useMenuContext } from '@srcDS/provider/menu/MenuContext';

interface HeaderProps {
  categories?: any;
  loggedIn?: boolean;
  customerGroupId?: string;
  locales?: any;
  storeLocatorLink?: string;
  accountDropdownContentBlock?: React.ReactNode;
  accountDropdownPromo?: React.ReactNode;
  promoText?: string;
  promoTextColor?: string;
  promoBackground?: string;
  handleLogin?: () => void;
  handleRegister?: () => void;
  isTransparent?: boolean;
  opacityLevel?: number;
  promoLink?: string | { target: string };
  shippingCoastMessage?: string;
  nostoBlocks?: any;
  isServerSideRendering?: boolean;
  crossBrandTraffic?: Brand[];
  contentfulNavigationTiles?: any;
  crossBrandTrafficColors?: CrossBrandTrafficProps['crossBrandTrafficColors'];
  siteLocale: string;
}

const Header: React.FC<HeaderProps> = ({
  categories = [],
  loggedIn,
  customerGroupId,
  locales = [],
  storeLocatorLink,
  accountDropdownContentBlock,
  accountDropdownPromo,
  promoText,
  promoTextColor,
  promoBackground,
  handleLogin,
  handleRegister,
  isTransparent = false,
  opacityLevel = 100,
  promoLink,
  shippingCoastMessage,
  nostoBlocks,
  isServerSideRendering,
  crossBrandTraffic,
  contentfulNavigationTiles,
  crossBrandTrafficColors,
  siteLocale,
}) => {
  const [hasMounted, setHasMounted] = useState(false);

  const { setActiveLevel1, setSelectedLevel2, setShownMenu, setCurrentLevelName } = useMenuContext();
  const { showSearchDropdown } = useLayoutContext();
  const { isRender, bannerHeight } = useGeoIpBanner();
  const [showPromotionalBanner, setShowPromotionalBanner] = useSessionStorage<boolean>('isShowPromotinalBanner', true);
  const stickyRef = useRef();
  const { pathname } = useLocation();
  const { isSticky } = useStickyHeader(stickyRef);
  const isMobile = useMedia('(max-width: 767px)', false);
  const isHomePage = pathname === '/';
  const scrollDirection = useScrollDirection(109);
  const { media, logoUrl, title } = useLogo();
  const { isTransparencyApplied, isInverted } = useHeaderEffects({
    isTransparent,
    isSticky,
    opacityLevel,
  });
  const { onClickWishlist, onClickPromo, toggleLoginForm, setToggleLoginForm } = useHeaderNavigation({
    loggedIn,
    isMobile,
    promoLink,
  });
  const notifications = useNotifications();
  const isInversedAndIsNotSticky = isInverted && !isSticky && !isServerSideRendering;
  const scrollingUpAndSticky = scrollDirection === 'up' && isSticky;
  const { processedBrands } = useCrossBrandTraffic({
    brands: crossBrandTraffic,
    siteLocale,
  });

  const hasCrossBrands =
    !!crossBrandTraffic &&
    crossBrandTraffic.length > 0 &&
    crossBrandTraffic?.every((item) => item.uniqueId && item.url && item.name);

  const handleMouseOut = () => {
    setActiveLevel1(null);
    setSelectedLevel2(null);
    setShownMenu(false);
    setCurrentLevelName(null);
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const promoStyles = useMemo(
    () =>
      ({
        '--promoBar-background': promoBackground || 'black',
        '--promoBar-text-color': promoTextColor || 'white',
      }) as React.CSSProperties,
    [promoBackground, promoTextColor],
  );

  const headerClasses = useMemo(
    () =>
      clsx(styles.header, {
        [styles.withoutPromo]: !showPromotionalBanner || !promoText,
        [styles.withCrossBrandComponent]: hasCrossBrands && processedBrands.length > 0,
      }),
    [showPromotionalBanner, promoText, crossBrandTraffic],
  );

  const promoClasses = useMemo(
    () =>
      clsx(styles.headerPromotional, {
        [styles.promoIsSticky]: scrollingUpAndSticky,
      }),
    [scrollingUpAndSticky],
  );

  return (
    <>
      <GeoIP />
      <div id="openSearchDropdown" className={headerClasses} style={promoStyles}>
        {showPromotionalBanner && promoText && hasMounted && (
          <div className={promoClasses}>
            <Marquee
              direction="left"
              isMobileVisible
              textColor={promoTextColor}
              backgroundColor={promoBackground}
              className={styles.headerPromotionalCenter}
              onClick={onClickPromo}
              isSSR={isServerSideRendering}
              promoText={promoText}
            >
              <SafeHTMLBlock text={promoText} containerClassname={styles.headerMarquee} />
            </Marquee>
            {/* @ts-ignore */}
            <Button
              className={styles.closeIcon}
              variant="primary"
              as="text"
              onClick={() => setShowPromotionalBanner(false)}
              ariaLabel="Close"
            >
              <CloseCross width="24" height="24" />
            </Button>
          </div>
        )}
        {crossBrandTraffic && processedBrands.length > 0 ? (
          <CrossBrandTraffic crossBrandTrafficColors={crossBrandTrafficColors} brands={processedBrands} />
        ) : null}
        {notifications.map((notification) => (
          <NotificationBar
            // @ts-ignore
            key={notification.id}
            id={
              typeof notification.id === 'string'
                ? notification.id
                : convertTextToId(String(notification.message || '') + String(notification.code || ''))
            }
            code={notification.code}
            message={notification.message}
            type={notification.type}
            timeout={notification.timeout}
          />
        ))}
        <div
          id="stickyHeader"
          onMouseLeave={handleMouseOut}
          className={clsx(styles.stickyHeader, {
            [styles.sticky]: isSticky && !isServerSideRendering,
            [styles.transparentHeader]: isTransparencyApplied && !isServerSideRendering,
            [styles.isInverted]: isInversedAndIsNotSticky,
            [styles.shownSearchPopup]: showSearchDropdown,
          })}
        >
          <div className={styles.headerContainer}>
            <div className={styles.headerNavigation}>
              <HeaderNavigation
                isMobile={isMobile}
                isInvertedClass={styles.isInvertedItem}
                locales={locales}
                categories={categories}
                storeLocatorLink={storeLocatorLink}
                isSSR={isServerSideRendering}
                contentfulNavigationTiles={contentfulNavigationTiles}
              />
              {isHomePage && (
                <span className={styles.headerLogo}>
                  {logoUrl && <ResponsiveImage source={media} title="Logo" />}
                  <span className={styles.headerTitle}>{logoUrl ? title : ''}</span>
                </span>
              )}
              {!isHomePage && logoUrl && (
                // @ts-ignore
                <Link ariaLabel="Logo" className={styles.headerLogo} link="/">
                  <ResponsiveImage source={media} title="Logo" />
                </Link>
              )}
            </div>
            <HeaderActions
              loggedIn={loggedIn}
              customerGroupId={customerGroupId}
              accountDropdownPromo={accountDropdownPromo}
              accountDropdownContentBlock={accountDropdownContentBlock}
              isInvertedClass={styles.isInvertedItem}
              handleRegister={handleRegister}
              handleLogin={handleLogin}
              showLoginForm={toggleLoginForm}
              setToggleLoginForm={setToggleLoginForm}
              isStickyHeader={isSticky}
              shippingCoastMessage={shippingCoastMessage}
              nostoBlocks={nostoBlocks}
              isMobile={isMobile}
              onClickWishlist={onClickWishlist}
              showMarquee={showPromotionalBanner}
            />
            <SearchInput isInvertedClass={styles.isInvertedItem} isHidden={scrollingUpAndSticky} />
          </div>
          <SecondaryMenuLevel
            geoBannerVisible={isRender}
            geoBannerHeight={bannerHeight}
            isStickyHeader={isSticky}
            isInvertedClass={styles.isInvertedItem}
            contentfulNavigationTiles={contentfulNavigationTiles}
            flattened={flattenedNavigation}
          />
        </div>
        <div ref={stickyRef} className={styles.hiddenBlock} />
      </div>
    </>
  );
};

export default Header;
